import {
  addToast,
  Button,
  DateInput,
  Modal,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { useActionKitImp } from '../../../../components/ozmoit/common/action-kit-imp';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { createWarningRecord } from '../../actions';
import { useWarningsSummaryDetailsFormLoaderData } from '../../loaders/StudentWarningsSummaryDetailsFormLoader';
import { useWarningsSummaryDetailsLoader } from '../../loaders/StudentWarningsSummaryDetailsLoader';
import { StudentWarningsGroupType } from '../../types';

export type BehaviorWarningGroupRecord = {
  id: number;
  title: string;
  date: string;
  notes: string | null;
  type: string;
  severity: string | null;
  penalization: string | null;
  conditionality: string | null;
  detentionDays: number;
};

export type BehaviorWarningGroupModalMethods = {
  open: (record?: BehaviorWarningGroupRecord) => void;
  close: () => void;
};

type BehaviorWarningGroupModalProps = {
  onSave?: () => void;
};

type FormFields = {
  title: string;
  date: string;
  notes?: string | null;
  type: SelectOptionType | null;
  severity: SelectOptionType | null;
  penalization: SelectOptionType | null;
  conditionality: SelectOptionType | null;
  detentionDays: SelectOptionType | null;
};

const DEFAULT_VALUES: FormFields = {
  title: '',
  date: '',
  notes: null,
  type: null,
  severity: null,
  penalization: null,
  conditionality: null,
  detentionDays: null,
};

const WARNING_TYPES = {
  INTERNAL_SANCTION: 'internal-sanction',
  PENALIZATION: 'penalization',
  OWN_INITIATIVE: 'own-initiative',
  CONDITIONALLY: 'conditionally',
};

const PENALIZATION_TYPES = {
  SIMPLE: 'simple',
  SERIOUS: 'serious',
  DETENTION_DAYS: 'detention-days',
};

const TYPE_SELECTOR_SIZE = {
  [WARNING_TYPES.INTERNAL_SANCTION]: 4,
  [WARNING_TYPES.PENALIZATION]: 8,
  [WARNING_TYPES.OWN_INITIATIVE]: 4,
  [WARNING_TYPES.CONDITIONALLY]: 4,
  NONE: 8,
};

const BehaviorWarningGroupModal = (
  { onSave }: BehaviorWarningGroupModalProps,
  ref: Ref<BehaviorWarningGroupModalMethods>,
) => {
  const data = useWarningsSummaryDetailsFormLoaderData();
  const { msgValidations } = useValidations();
  const { query } = useWarningsSummaryDetailsLoader();
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_DETAILS);
  const [visible, setVisible] = useState(false);
  const [record, setRecord] = useState<BehaviorWarningGroupRecord>();

  const { control, handleSubmit, reset, watch } = useForm<FormFields>({
    defaultValues: DEFAULT_VALUES,
  });

  const [warningType, penalizationType, notesValue] = watch([
    'type',
    'penalization',
    'notes',
  ]);

  const notesCount = useMemo(() => {
    return notesValue?.length ?? 0;
  }, [notesValue]);

  const { action: handleClickSave, loading } = useActionKitImp(
    createWarningRecord,
    {
      onSuccess: () => {
        addToast({
          color: 'success',
          text: t(`group.behavior.modal.success`),
        });
        !!onSave && onSave();
        setVisible(false);
        setRecord(undefined);
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            text: t(`group.behavior.modal.failed`),
          });
        },
      },
    },
  );

  const handleOpen = useCallback(
    (nextRecord?: BehaviorWarningGroupRecord) => {
      if (nextRecord) {
        reset({
          title: nextRecord.title,
          date: nextRecord.date,
          notes: nextRecord.notes?.trim()?.slice(0, 150) || null,
          type:
            data?.behaviorRecordTypes.find(
              (option) => option.value === nextRecord.type,
            ) || null,
          severity:
            data?.behaviorRecordSeverities.find(
              (option) => option.value === nextRecord.severity,
            ) || null,
          penalization:
            data?.behaviorRecordPenalizations.find(
              (option) => option.value === nextRecord.penalization,
            ) || null,
          conditionality:
            data?.behaviorRecordConditionalities.find(
              (option) => option.value === nextRecord.conditionality,
            ) || null,
          detentionDays:
            data?.behaviorRecordDetentionDays.find(
              (option) => option.value === nextRecord.detentionDays,
            ) || null,
        });
      } else {
        reset(DEFAULT_VALUES);
      }
      setVisible(true);
      setRecord(nextRecord);
    },
    [data, reset],
  );

  const handleClose = useCallback(() => {
    setVisible(false);
    setRecord(undefined);
  }, []);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const handleSave = useCallback(
    async (data: FormFields) => {
      if (loading) {
        return;
      }
      let payload: Record<string, any> = {
        studyPlanEnrollmentId: query.studyPlanEnrollmentId,
        title: data?.title,
        date: data?.date,
        notes: data?.notes,
        type: data?.type?.value,
      };

      switch (data.type?.value) {
        case WARNING_TYPES.OWN_INITIATIVE:
          payload = {
            ...payload,
            penalization: data?.penalization?.value,
            detentionDays:
              data?.penalization?.value === PENALIZATION_TYPES.DETENTION_DAYS
                ? data?.detentionDays?.value || 0
                : 0,
          };
          break;
        case WARNING_TYPES.INTERNAL_SANCTION:
          payload = {
            ...payload,
            severity: data?.severity?.value,
          };
          break;
        case WARNING_TYPES.CONDITIONALLY:
          payload = {
            ...payload,
            conditionality: data?.conditionality?.value,
          };
          break;
      }
      handleClickSave(StudentWarningsGroupType.Behavior, payload);
    },
    [loading, query, handleClickSave],
  );

  return (
    <Modal isOpen={!!visible} size="lg" toggle={handleClose}>
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h2 className="text-dark fs-22 mb-0 w-100 text-center">
              {record?.id
                ? t(`group.behavior.modal.titleEdit`)
                : t(`group.behavior.modal.title`)}
            </h2>
            <p className="fs-16 w-100 text-center">
              {record?.id
                ? t(`group.behavior.modal.descriptionEdit`)
                : t(`group.behavior.modal.description`)}
            </p>
          </Col>
        </Row>
        <Row className="my-5">
          <Col
            xs={12}
            md={TYPE_SELECTOR_SIZE[warningType?.value || 'NONE'] || 12}
          >
            <Select
              control={control}
              name="type"
              options={data?.behaviorRecordTypes || []}
              label={t(`group.behavior.table.type`)}
              isClearable={false}
              rules={{ required: msgValidations.required }}
              disabled={!!record?.id}
            />
          </Col>
          {warningType?.value === WARNING_TYPES.INTERNAL_SANCTION && (
            <Col xs={12} md={4}>
              <Select
                control={control}
                name="severity"
                options={data?.behaviorRecordSeverities || []}
                label={t(`group.behavior.table.severity`)}
                isClearable={false}
                rules={{ required: msgValidations.required }}
                disabled={!!record?.id}
              />
            </Col>
          )}
          <Col xs={12} md={4}>
            <DateInput
              control={control}
              name="date"
              label={t(`group.behavior.table.date`)}
              rules={{ required: msgValidations.required }}
              maxDate={new Date()}
              disabled={!!record?.id || !warningType}
            />
          </Col>
          {warningType?.value === WARNING_TYPES.CONDITIONALLY && (
            <Col xs={12} md={4}>
              <Select
                control={control}
                name="conditionality"
                options={data?.behaviorRecordConditionalities || []}
                label={t(`group.behavior.table.conditionality`)}
                isClearable={false}
                rules={{ required: msgValidations.required }}
                disabled={!!record?.id}
              />
            </Col>
          )}
          {warningType?.value === WARNING_TYPES.OWN_INITIATIVE && (
            <Col xs={12} md={4}>
              <Select
                control={control}
                name="penalization"
                options={data?.behaviorRecordPenalizations || []}
                label={t(`group.behavior.table.penalization`)}
                isClearable={false}
                rules={{ required: msgValidations.required }}
                disabled={!!record?.id}
              />
            </Col>
          )}
          {!!(
            warningType?.value === WARNING_TYPES.OWN_INITIATIVE &&
            penalizationType?.value === PENALIZATION_TYPES.DETENTION_DAYS
          ) && (
            <Col xs={12} md={4}>
              <Select
                control={control}
                name="detentionDays"
                options={data?.behaviorRecordDetentionDays || []}
                label={t(`group.behavior.table.detentionDays`)}
                isClearable={false}
                rules={{ required: msgValidations.required }}
                disabled={!!record?.id}
              />
            </Col>
          )}
          <Col xs={12} md={12}>
            <TextInput
              control={control}
              name="title"
              label={t(`group.behavior.table.title`)}
              rules={{ required: msgValidations.required }}
              disabled={!!record?.id || !warningType}
            />
          </Col>
          <Col className="mb-5" xs={12} md={12}>
            <TextAreaInput
              control={control}
              name="notes"
              label={t(`group.behavior.table.notes`)}
              rules={{
                required: false,
              }}
              disabled={!!record?.id || !warningType}
              maxLength={150}
            />
            <p>{`${notesCount} / 150`}</p>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={record?.id ? 12 : 6}>
            <Button
              text={
                record?.id
                  ? t('common:actions.close')
                  : t('common:actions.cancel')
              }
              type="button"
              fullwidth
              outlined
              onClick={handleClose}
            />
          </Col>
          {!record?.id && (
            <Col xs={12} md={6}>
              <Button
                text={t('common:actions.add')}
                type="button"
                fullwidth
                onClick={handleSubmit(handleSave)}
                loading={loading}
                disabled={!warningType}
              />
            </Col>
          )}
        </Row>
      </Container>
    </Modal>
  );
};

export default forwardRef(BehaviorWarningGroupModal);
